import { useMutation, useQueryClient } from "@tanstack/react-query";
import { TherapiesKeys } from "./therapies.keys";
import { masterTablesKeys } from "./masterTables.keys";
import { useApi } from "../../api/ApiProvider";
import notificationStore from "../Notification/notificationStore";
import type {
  CreateTherapyDto,
  UpdateTherapyDto,
  CreatePriceDateRangeDto,
  UpdatePriceDateRangeDto,
  CreateDiagnosisGroupDto,
  UpdateDiagnosisGroupDto,
  AiPriceProcessingRequestDto,
  BulkChangeItemDto,
  UpsertPrescribedTherapyDto,
  UpsertTherapyMethodDto,
} from "../../api-client";

// ============= THERAPIES =============

export const useCreateTherapy = () => {
  const qc = useQueryClient();
  const { masterlists } = useApi();
  return useMutation({
    mutationFn: (data: CreateTherapyDto) => masterlists.createTherapy(data),
    onSuccess: () => {
      notificationStore.showMessage("Therapie erfolgreich erstellt", "success");
      qc.invalidateQueries({ queryKey: TherapiesKeys.therapies.all() });
    },
    onError: (e: any) =>
      notificationStore.showMessage(e?.response?.data?.message || e?.message || "Fehler beim Erstellen", "error", e),
  });
};

export const useUpdateTherapy = () => {
  const qc = useQueryClient();
  const { masterlists } = useApi();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTherapyDto }) => masterlists.updateTherapy(id, data),
    onSuccess: () => {
      notificationStore.showMessage("Therapie aktualisiert", "success");
      qc.invalidateQueries({ queryKey: TherapiesKeys.therapies.all() });
    },
    onError: (e: any) =>
      notificationStore.showMessage(e?.response?.data?.message || e?.message || "Fehler beim Aktualisieren", "error", e),
  });
};

export const useDeleteTherapy = () => {
  const qc = useQueryClient();
  const { masterlists } = useApi();
  return useMutation({
    mutationFn: (id: string) => masterlists.deleteTherapy(id),
    onSuccess: () => {
      notificationStore.showMessage("Therapie gelöscht", "success");
      qc.invalidateQueries({ queryKey: TherapiesKeys.therapies.all() });
    },
    onError: (e: any) =>
      notificationStore.showMessage(e?.response?.data?.message || e?.message || "Fehler beim Löschen", "error", e),
  });
};

// ============= PRICES =============

export const useCreatePrice = () => {
  const qc = useQueryClient();
  const { masterlists } = useApi();
  return useMutation({
    mutationFn: (data: CreatePriceDateRangeDto) => masterlists.createPrice(data),
    onSuccess: () => {
      notificationStore.showMessage("Preis erstellt", "success");
      qc.invalidateQueries({ queryKey: TherapiesKeys.prices.all() });
      qc.invalidateQueries({ queryKey: TherapiesKeys.therapies.all() });
    },
    onError: (e: any) =>
      notificationStore.showMessage(e?.response?.data?.message || e?.message || "Fehler beim Erstellen", "error", e),
  });
};

export const useUpdatePrice = () => {
  const qc = useQueryClient();
  const { masterlists } = useApi();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePriceDateRangeDto }) => masterlists.updatePrice(id, data),
    onSuccess: () => {
      notificationStore.showMessage("Preis aktualisiert", "success");
      qc.invalidateQueries({ queryKey: TherapiesKeys.prices.all() });
      qc.invalidateQueries({ queryKey: TherapiesKeys.therapies.all() });
    },
    onError: (e: any) =>
      notificationStore.showMessage(e?.response?.data?.message || e?.message || "Fehler beim Aktualisieren", "error", e),
  });
};

export const useDeletePrice = () => {
  const qc = useQueryClient();
  const { masterlists } = useApi();
  return useMutation({
    mutationFn: (id: string) => masterlists.deletePrice(id),
    onSuccess: () => {
      notificationStore.showMessage("Preis gelöscht", "success");
      qc.invalidateQueries({ queryKey: TherapiesKeys.prices.all() });
      qc.invalidateQueries({ queryKey: TherapiesKeys.therapies.all() });
    },
    onError: (e: any) =>
      notificationStore.showMessage(e?.response?.data?.message || e?.message || "Fehler beim Löschen", "error", e),
  });
};

// ============= DIAGNOSIS GROUPS =============

export const useCreateDiagnosisGroup = () => {
  const qc = useQueryClient();
  const { masterlists } = useApi();
  return useMutation({
    mutationFn: (data: CreateDiagnosisGroupDto) => masterlists.createDiagnosisGroup(data),
    onSuccess: () => {
      notificationStore.showMessage("Diagnosegruppe erstellt", "success");
      qc.invalidateQueries({ queryKey: TherapiesKeys.diagnosisGroups.all() });
    },
    onError: (e: any) =>
      notificationStore.showMessage(e?.response?.data?.message || e?.message || "Fehler beim Erstellen", "error", e),
  });
};

export const useUpdateDiagnosisGroup = () => {
  const qc = useQueryClient();
  const { masterlists } = useApi();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDiagnosisGroupDto }) => masterlists.updateDiagnosisGroup(id, data),
    onSuccess: () => {
      notificationStore.showMessage("Diagnosegruppe aktualisiert", "success");
      qc.invalidateQueries({ queryKey: TherapiesKeys.diagnosisGroups.all() });
    },
    onError: (e: any) =>
      notificationStore.showMessage(e?.response?.data?.message || e?.message || "Fehler beim Aktualisieren", "error", e),
  });
};

export const useDeleteDiagnosisGroup = () => {
  const qc = useQueryClient();
  const { masterlists } = useApi();
  return useMutation({
    mutationFn: (id: string) => masterlists.deleteDiagnosisGroup(id),
    onSuccess: () => {
      notificationStore.showMessage("Diagnosegruppe gelöscht", "success");
      qc.invalidateQueries({ queryKey: TherapiesKeys.diagnosisGroups.all() });
    },
    onError: (e: any) =>
      notificationStore.showMessage(e?.response?.data?.message || e?.message || "Fehler beim Löschen", "error", e),
  });
};

// ============= AI PROCESSING =============

export const useProcessAiPriceText = () => {
  const { masterlists } = useApi();
  return useMutation({
    mutationFn: (data: AiPriceProcessingRequestDto) =>
      masterlists.processAiPriceText(data),
    onError: (e: any) =>
      notificationStore.showMessage(e?.response?.data?.message || e?.message || "KI-Verarbeitung fehlgeschlagen", "error", e),
  });
};

// ============= BULK SAVE =============

export const useBulkSave = () => {
  const qc = useQueryClient();
  const { masterlists } = useApi();
  return useMutation({
    mutationFn: (changes: BulkChangeItemDto[]) => masterlists.bulkSaveChanges({ changes }),
    onSuccess: (data: any) => {
      const msg = data.errors?.length
        ? `${data.appliedCount} Änderungen gespeichert, ${data.errors.length} Fehler`
        : `${data.appliedCount} Änderungen erfolgreich gespeichert`;
      notificationStore.showMessage(msg, data.errors?.length ? "warning" : "success");
      qc.invalidateQueries({ queryKey: TherapiesKeys.therapies.all() });
      qc.invalidateQueries({ queryKey: TherapiesKeys.prices.all() });
      qc.invalidateQueries({ queryKey: TherapiesKeys.diagnosisGroups.all() });
    },
    onError: (e: any) =>
      notificationStore.showMessage(e?.response?.data?.message || e?.message || "Fehler beim Speichern", "error", e),
  });
};

// ============= MD-232 CATALOG =============
//
// Every catalog mutation invalidates the graph as well as its own list: editing
// a term's resolutions changes which tarif groups it is reachable in, and that
// is the number the Katalog tab shows.

const invalidateCatalog = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: [TherapiesKeys.catalogTerms.scope] });
  qc.invalidateQueries({ queryKey: [TherapiesKeys.catalogMethods.scope] });
  qc.invalidateQueries({ queryKey: [TherapiesKeys.catalogGraph.scope] });
  // GetTherapyColors is reference data cached for 12h, which is right for every
  // client EXCEPT the one that just changed a colour: without this the admin
  // sees the new swatch in the table while the calendar next door keeps drawing
  // the old colour until the cache ages out.
  qc.invalidateQueries({ queryKey: masterTablesKeys.therapyColors.all() });
};

const catalogError = (e: any, fallback: string) =>
  notificationStore.showMessage(
    e?.response?.data?.message || e?.message || fallback,
    "error",
    e
  );

export const useCreateCatalogTerm = () => {
  const qc = useQueryClient();
  const { masterlists } = useApi();
  return useMutation({
    mutationFn: (data: UpsertPrescribedTherapyDto) => masterlists.createCatalogTerm(data),
    onSuccess: () => {
      notificationStore.showMessage("Begriff erstellt", "success");
      invalidateCatalog(qc);
    },
    onError: (e: any) => catalogError(e, "Begriff konnte nicht erstellt werden"),
  });
};

export const useUpdateCatalogTerm = () => {
  const qc = useQueryClient();
  const { masterlists } = useApi();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpsertPrescribedTherapyDto }) =>
      masterlists.updateCatalogTerm(id, data),
    onSuccess: () => {
      notificationStore.showMessage("Begriff gespeichert", "success");
      invalidateCatalog(qc);
    },
    onError: (e: any) => catalogError(e, "Begriff konnte nicht gespeichert werden"),
  });
};

export const useDeleteCatalogTerm = () => {
  const qc = useQueryClient();
  const { masterlists } = useApi();
  return useMutation({
    mutationFn: (id: string) => masterlists.deleteCatalogTerm(id),
    onSuccess: () => {
      notificationStore.showMessage("Begriff gelöscht", "success");
      invalidateCatalog(qc);
    },
    // the server refuses a delete that prescription data still depends on and
    // says exactly what blocks it — surface that verbatim
    onError: (e: any) => catalogError(e, "Begriff konnte nicht gelöscht werden"),
  });
};

export const useCreateCatalogMethod = () => {
  const qc = useQueryClient();
  const { masterlists } = useApi();
  return useMutation({
    mutationFn: (data: UpsertTherapyMethodDto) => masterlists.createCatalogMethod(data),
    onSuccess: () => {
      notificationStore.showMessage("Methode erstellt", "success");
      invalidateCatalog(qc);
    },
    onError: (e: any) => catalogError(e, "Methode konnte nicht erstellt werden"),
  });
};

export const useUpdateCatalogMethod = () => {
  const qc = useQueryClient();
  const { masterlists } = useApi();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpsertTherapyMethodDto }) =>
      masterlists.updateCatalogMethod(id, data),
    onSuccess: () => {
      notificationStore.showMessage("Methode gespeichert", "success");
      invalidateCatalog(qc);
    },
    onError: (e: any) => catalogError(e, "Methode konnte nicht gespeichert werden"),
  });
};

export const useDeleteCatalogMethod = () => {
  const qc = useQueryClient();
  const { masterlists } = useApi();
  return useMutation({
    mutationFn: (id: string) => masterlists.deleteCatalogMethod(id),
    onSuccess: () => {
      notificationStore.showMessage("Methode gelöscht", "success");
      invalidateCatalog(qc);
    },
    onError: (e: any) => catalogError(e, "Methode konnte nicht gelöscht werden"),
  });
};
